import { SCHEMA } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import React, { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumber,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { BORDER_OPTIONS } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { ReflectionFormBrandType } from './reflectionTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.reflection.style, {
  common: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 100, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 10, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  input_hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  input_focus: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.focusColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.testKeyboardInput.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  downloadButton: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  button_hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  icon: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.list.schema.backgroundImage'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.iconColor'),
      useConfigs: [useBrandColorOptions],
    }),
    height: genBrandField({
      as: FieldNumber,
      label: t('input.label.height'),
      rules: [{ min: 1, message: ' ' }],
      props: { width: 64 },
    }),
    width: genBrandField({
      as: FieldNumber,
      label: t('input.label.width'),
      rules: [{ min: 1, message: ' ' }],
      props: { width: 64 },
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 100, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
  },
})

const ReflectionFormStyle: ReflectionFormBrandType = ({ device, subConfigs, edited, setForm }) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom gap sides>
        <Typography weight='medium'>{t('elements.reflection.tags.common')}</Typography>
        <FormField config={schema.common.backgroundColor} />
        <FormField config={schema.common.borderRadius} />
        <FormField config={schema.common.borderStyle} />
        <FormField config={schema.common.borderColor} />
        <FormField config={schema.common.borderWidth} />
        <FormField config={schema.input_hover.backgroundColor} />
        <FormField config={schema.input_focus.backgroundColor} />

        <Typography weight='medium'>{t('elements.reflection.tags.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <Typography weight='medium'>{t('elements.reflection.tags.downloadButton')}</Typography>
        <FormField config={schema.downloadButton.backgroundColor} />
        <FormField config={schema.button_hover.backgroundColor} />
        <FormField config={schema.icon.backgroundImage} />
        <FormField config={schema.icon.color} />
        <FormField config={schema.icon.height} />
        <FormField config={schema.icon.width} />
        <FormField config={schema.icon.borderRadius} />

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(ReflectionFormStyle) as typeof ReflectionFormStyle
